import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import NsContent from 'components/common/ns-content/NsContent';

import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { NsCheckboxInput } from 'components/common/ns-checkbox-input/NsCheckboxInput';
import { NsInputterValidations } from 'components/common/ns-inputter/NsInputterValidations';
import { NsList } from 'components/common/ns-list/NsList';
import { actionUrl } from 'config/static-content';
import { stringToBool } from 'utils/misc';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { PARTNERS } from 'config/app-config';
import { selectIsLoading, selectHasPsr, setHasPsr } from 'state/psr/psr-slice';
import { getPsrThunk } from 'state/psr/psr-thunks';
import { selectErrors } from 'state/psr/psr-slice';
import { useNavigation } from 'hooks/useNavigation';

export const testId = 'psrEntryTestId';

export const PsrEntry = () => {
  const hasPsr = useSelector(selectHasPsr, shallowEqual);
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const errors = useSelector(selectErrors, shallowEqual);
  const isLoginEnabledJourney = useIsLoginEnabledJourney({
    exclude: [PARTNERS.NONDOMESTIC_TRANSFER]
  });
  const { eventTypes, next } = useNavigation();

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      const boolValue = stringToBool(value);

      dispatch(setHasPsr(boolValue));
    },
    [dispatch]
  );

  const handleGoNext = useCallback(async () => {
    if (hasPsr) {
      try {
        await dispatch(getPsrThunk());
        next({ eventType: eventTypes.NEXT });
      } catch (e) {
        e && console.error(e);
      }
    } else {
      next({ eventType: eventTypes.NEXT });
    }
  }, [next, eventTypes.NEXT, hasPsr, dispatch]);

  return useMemo(
    () => (
      <NsPanel testId={testId}>
        <NsBackCta />
        <NsContent>
          <NsTitle title="Priority Services Register" />
          {!isLoginEnabledJourney ? (
            <p>
              The Priority Services Register is a free support service that
              gives help to customers who need it. If you are living with
              certain health difficulties, access needs, disability, or find
              yourself living in challenging circumstances, our Priority
              Services Register may be able to help you with:
            </p>
          ) : (
            <p>
              When we supply your energy, we’ll look out for your safety. If
              anyone at your home is living with certain health difficulties,
              access needs, disability, or find yourself living in challenging
              circumstances, our Priority Services Register may be able to help
              you with:
            </p>
          )}
          <NsList className="ul-bullet">
            {[
              'Notice of planned power cuts',
              'Braille and audio letters',
              'Free gas safety checks',
              'Help you with meter reads'
            ]}
          </NsList>
          {!isLoginEnabledJourney && (
            <>
              <p>
                When you sign up to the PSR, we’ll store and manage your data on
                our secure systems. You can read more about how we use your
                information above in our{' '}
                <NsAnchor href={actionUrl.privacy}>
                  Energy privacy notice.
                </NsAnchor>
              </p>

              <p>
                Sometimes, we’ll share it with those who supply and maintain the
                supply of electricity & gas to your home, including meter
                operators, engineering partners, network operators and water
                suppliers.
              </p>
            </>
          )}
        </NsContent>
        <NsForm onSubmit={handleGoNext}>
          <NsRadioInput
            name="psrEntry"
            dataTestId="radioPsrEntryTestId"
            onChange={handleChange}
            value={hasPsr}
            heading="Does anyone in your home need extra help?"
            options={[
              {
                label: 'Yes, add me to the Priority Services Register',
                value: 'true'
              },
              {
                label: `No, I don't need additional support`,
                value: 'false'
              }
            ]}
          />
          {isLoginEnabledJourney && hasPsr && (
            <NsCheckboxInput
              name="terms"
              testId="psrConsentTestId"
              options={[
                {
                  label: (
                    <>
                      Do you consent to us adding you to the Priority Services
                      Register and sharing your information? We’ll only share
                      this with those who need to know, like gas transporters,
                      electricity distributors and water suppliers, so if
                      there’s a loss of supply or power outage you may be
                      treated as a priority customer.
                    </>
                  ),
                  value: 'true'
                }
              ]}
              validation={[NsInputterValidations.isRequired]}
            />
          )}
          <Error errors={errors} />
          <NsPrimaryCta isLoading={isLoading}>Continue</NsPrimaryCta>
        </NsForm>
      </NsPanel>
    ),
    [
      isLoading,
      hasPsr,
      errors,
      isLoginEnabledJourney,
      handleChange,
      handleGoNext
    ]
  );
};

export default PsrEntry;
